/**
 * THE CONTACT TREATMENT: what the water does where it meets a thing the camera can see.
 *
 * Measured by the living-sea lane (gauntlet/living-sea/OBJECT-WATERLINE.md) on the confirmed
 * reference frames and specified at grain 2 in its section 7. On the WATER, running down-screen
 * from the visible silhouette edge:
 *
 *   px 1        the RIM: 1 art pixel, value +46, saturation x0.45, hue held.
 *   px 1..9     the SHADOW: multiplicative on value, deepest x0.80, the measured 9-px ramp;
 *               the rim replaces the shadow's first pixel where the rim is drawn.
 *
 * VISIBLE CONTACTS ONLY. MEASURED on 1,139 occluded rays: the water on the far side of a hull
 * top, a dock top or a far shore carries nothing (per-offset medians within 1.4 luma of the water,
 * 0 of 127 rim rays on the water side). So the treatment is drawn where a silhouette pixel has
 * water directly BELOW it on screen, and nowhere else.
 *
 * ANGLE. The reference rim traces the lower curve of every hull and stops where the contact
 * turns vertical; the shadow is on 99% of a straight bank's rays and 94% of hull rays. The lane's
 * angle model is a GUESS with two anchors (section 5c): shadow strength = clamp(n.y, 0, 1), rim
 * strength = clamp((n.y - 0.5) / 0.5, 0, 1). This file applies it as follows, labelled GUESS:
 * the rim is drawn where rim strength >= 0.5 (the edge's outward normal within ~41 degrees of
 * straight down-screen), and the shadow keeps its six measured values and loses its FAR ROWS on
 * steeper edges (rows = round(9 * shadow strength), the ramp cut short) rather than fading, so no
 * value the reference does not have is ever invented.
 *
 * THE SEA IS NO LONGER A CONSTANT FILL (round 4: open water is POKOMOKO's moving tile), so the
 * band comes back as TWO overlays the width of the sprite and BAND_PX taller, both drawn UNDER
 * the sprite: the rim as opaque RIM_RGB pixels, and the shadow as grey MULTIPLY factors
 * (palette.ts SHADOW_FACTORS) that darken whatever the water shows there - base, mid or crest -
 * by the measured ramp. "Multiplicative on value, hue and saturation held" is exactly a
 * per-channel multiply, so this is the measured treatment applied to a moving surface rather
 * than a flat patch of the old sea pasted over it. Neither overlay ever writes a silhouette
 * pixel, so neither touches the material mask. Every number above is unchanged.
 */

import { RIM_RGB, SHADOW_FACTORS, SHADOW_RAMP, type Rgb } from './palette';
import { foamAt } from './sea';
import { SHELF_ROCK, materialOf } from './shelf';

export const SHADOW_PX = SHADOW_RAMP.length;
/** Rows the overlay may need below the sprite's own box: the shadow's full depth. */
export const BAND_PX = SHADOW_PX;

/** Rim strength threshold, GUESS (section 5c's 0.5 on the (n.y - 0.5) / 0.5 ramp). */
export const RIM_MIN_NY = 0.75;

/**
 * THE FOAM (round 9, CONTRACT 1.5w, `?water=render` only): a THIRD plane in the same band.
 *
 * -- ONE TREATMENT ON EVERY SHORELINE IS WRONG ON TWO OF THE THREE REFERENCES ------------------
 *
 * woolstrand-8302 has white surf along its sand beach. blackstage-8325 has bare rock with no foam
 * at all - MEASURED, its shelf rectangle's bright fraction over p50+60 is 0.046%, against
 * woolstrand's 8.288%. So the width is a function of the MATERIAL of the land pixel the foam is
 * under, read by shelf.ts's classifier off the sprite's own bytes: sand widest, a vegetated or
 * pale-grey lip narrower, ROCK ZERO.
 *
 * -- WHY SAND IS 9 AND NOT 10, WHICH IS A CORRECTION TO THE ROUND'S OWN DESIGN -----------------
 *
 * The design asked for sand 10, lip 6, rock 0 art px "inside the existing w x (h + 9) band". A
 * band 9 rows deep cannot hold a 10 px treatment: the write loop breaks at `py >= H`, so a sand
 * column at full width would have drawn 9 rows and quietly reported 10. Widening BAND_PX instead
 * would move `bandH` on BOTH arms and re-bake every contact texture in the game to pay for a
 * pixel. So the width is the band's own depth, and the number is stated here rather than lost.
 *
 * -- THE FADE: THE ROUND'S OWN DESIGN WAS BACKWARDS, AND HERE IS THE COUNTER-EXAMPLE ----------
 *
 * The design said `1 - smoothstep(0.55, 0.75, n.y)` - foam where the contact's outward normal is
 * NOT close to straight down-screen - so that foam and the rim (RIM_MIN_NY 0.75) would be
 * complementary. Complementary they are. They are also complementary in the wrong direction, and
 * the consequence was MEASURED on the round's own shipped captures by a critic:
 *
 *   open-sea-render.png     0 foam px
 *   murrin-roost-render.png 19 sand + 110 lip px, 0.05% of its 245,758 water px
 *   torsk-vard-render.png   0 sand + 227 lip px, 0.07% of 315,763
 *   per-column depth        median 2 art px, max 10, against a stated lip width of ~5.4
 *
 * against reference crops carrying 5.64% (woolstrand), 6.47% (blackstage) and 27.03% (almadra)
 * of their pixels above their own p50+40. "white foam at the beach" - the thing the owner named
 * first - was invisible in all six captures.
 *
 * WHY IT COMES OUT THAT WAY. A top-down island's lower silhouette is mostly FLAT: n.y near 1
 * along most of its length, which is exactly where `1 - smoothstep` returns 0. The columns that
 * did get foam were the near-vertical ones at the ends of a headland, where the water below the
 * edge is a sliver. So the treatment was drawn precisely where it could not be seen.
 *
 * WHAT THE REFERENCE ACTUALLY SHOWS. woolstrand-8302's surf runs along the beach FACING THE
 * VIEWER - the flat part - and blackstage-8325's bare rock has none anywhere. So the axis that
 * decides how WIDE the foam is is the MATERIAL, which this file already reads, and the axis n.y
 * decides is whether there is water below the edge at all - the same question the SHADOW asks,
 * and it answers it with `clamp(n.y, 0, 1)`.
 *
 * So the fade is `smoothstep(FOAM_NY_LO, FOAM_NY_HI, n.y)`: the same ramp, read the other way.
 * The rim reconciliation survives inverted, and is now the stronger statement: where a shore is
 * sand or a pale lip the foam REPLACES the rim's one pixel and widens it into surf (the write
 * loop already gives foam the pixel); where a shore is rock the foam is 0 and the measured 1 px
 * rim is all there is - which is blackstage exactly.
 *
 * -- THE FRINGE ---------------------------------------------------------------------------------
 *
 * The outer edge is irregular, from `sea.ts foamAt` on the sprite's identity and the column. The
 * design says "hash32 of the world column"; a sprite's world position is fixed for the life of
 * the world, so hashing the sprite and its own column is hashing the world column, and it is the
 * only one of the two that EXISTS when the band is built. The boat - the one sprite that moves -
 * never gets foam at all, which is also the right answer: a hull is not a shore.
 */
export interface FoamSpec {
  /** The sprite's identity, so its fringe is its own and is the same on every reload. */
  readonly key: number;
  /** Art px of reach, indexed by shelf.ts material: [sand, lip, rock]. Clamped to BAND_PX. */
  readonly widthPx: readonly [number, number, number];
  /** The colour, indexed the same way. palette.ts `foamFor`. */
  readonly rgb: readonly [Rgb, Rgb, Rgb];
  /** The sprite's own pixels, to read the material of the land pixel each column's foam is under. */
  readonly rgba: Uint8ClampedArray | Uint8Array;
}

export interface ContactBand {
  readonly w: number;
  readonly h: number;
  /** The rim: opaque RIM_RGB where drawn, alpha 0 elsewhere. Normal blend. */
  readonly rim: Uint8ClampedArray;
  /** The shadow: grey (k, k, k, 255) with k from SHADOW_FACTORS where drawn, alpha 0 elsewhere. MULTIPLY blend. */
  readonly shadow: Uint8ClampedArray;
  /** The foam: opaque, alpha 0 elsewhere, null when no FoamSpec was given (the settled arm). */
  readonly foam: Uint8ClampedArray | null;
  /** Pixels written as rim / as shadow / as foam, for the record. */
  readonly rimPx: number;
  readonly shadowPx: number;
  readonly foamPx: number;
  /** Columns of foam by material, for the record: [sand, lip, rock]. */
  readonly foamColumns: readonly [number, number, number];
}

/**
 * The lower silhouette edges of a mask, per column: rows y where mask[y][x] is set and
 * mask[y+1][x] is not (or y is the last row). Concave shapes have several per column.
 */
function bottomEdges(mask: Uint8Array, w: number, h: number): number[][] {
  const edges: number[][] = [];
  for (let x = 0; x < w; x++) {
    const col: number[] = [];
    for (let y = 0; y < h; y++) {
      if (!mask[y * w + x]) continue;
      if (y === h - 1 || !mask[(y + 1) * w + x]) col.push(y);
    }
    edges.push(col);
  }
  return edges;
}

/** The bottom edge in column `x` nearest to row `y`, within `reach` rows, or null. */
function nearestEdge(edges: number[][], x: number, y: number, reach: number): number | null {
  const col = edges[x];
  if (!col) return null;
  let best: number | null = null;
  for (const e of col) {
    const d = Math.abs(e - y);
    if (d <= reach && (best === null || d < Math.abs(best - y))) best = e;
  }
  return best;
}

/**
 * n.y of the outward normal at a bottom-edge pixel, estimated from the neighbouring columns'
 * bottom edges: slope = dy/dx across up to two columns each side, n.y = 1 / sqrt(1 + slope^2).
 * An isolated column (a 1-px spike) counts as flat. GUESS-grade geometry for a GUESS-grade model.
 */
function normalY(edges: number[][], x: number, y: number): number {
  let xl = -1;
  let yl = 0;
  let xr = -1;
  let yr = 0;
  for (const dx of [2, 1]) {
    const e = nearestEdge(edges, x - dx, y, 4);
    if (e !== null) { xl = x - dx; yl = e; break; }
  }
  for (const dx of [2, 1]) {
    const e = nearestEdge(edges, x + dx, y, 4);
    if (e !== null) { xr = x + dx; yr = e; break; }
  }
  if (xl < 0 && xr < 0) return 1;
  if (xl < 0) { xl = x; yl = y; }
  if (xr < 0) { xr = x; yr = y; }
  const slope = (yr - yl) / (xr - xl);
  return 1 / Math.sqrt(1 + slope * slope);
}

/** `rim` is the rim colour for the base the band will sit on (palette.ts `rimFor`); the navy's by default. */
/** GUESS with two measured anchors: foam where the rim is not, and nothing in between at full. */
/** How far up the silhouette the material sample may walk. GUESS: 5 art px. */
export const FOAM_MATERIAL_UP = 5;
export const FOAM_NY_LO = 0.55;
export const FOAM_NY_HI = 0.75;
/**
 * THE FRINGE'S OWN FLOOR. `sea.ts foamAt` returns 0.25..0.85 about a mean of 0.55, so a lip
 * column at its stated 5.4 px reached 3 px on average and 1 px at the low end - which is what
 * the critic measured (median 2 art px). The fringe is meant to make the OUTER EDGE irregular,
 * not to halve the treatment, so it is remapped onto FOAM_FRINGE_LO..1 of the material's width.
 */
export const FOAM_FRINGE_LO = 0.62;
const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export function contactBand(mask: Uint8Array, w: number, h: number, rim: Rgb = RIM_RGB, foam: FoamSpec | null = null): ContactBand {
  const H = h + BAND_PX;
  const rimPlane = new Uint8ClampedArray(w * H * 4);
  const shadowPlane = new Uint8ClampedArray(w * H * 4);
  const foamPlane = foam === null ? null : new Uint8ClampedArray(w * H * 4);
  const edges = bottomEdges(mask, w, h);
  let rimPx = 0;
  let shadowPx = 0;
  let foamPx = 0;
  const foamColumns: [number, number, number] = [0, 0, 0];
  for (let x = 0; x < w; x++) {
    const col = edges[x];
    if (!col) continue;
    for (const y of col) {
      const ny = normalY(edges, x, y);
      const rimHere = ny >= RIM_MIN_NY;
      const rows = Math.round(SHADOW_PX * Math.max(0, Math.min(1, ny)));
      // THE FOAM'S REACH IN THIS COLUMN: the material under it, faded out where the rim fades in,
      // and cut to an irregular fringe. 0 at rock, always.
      let foamRows = 0;
      let material = SHELF_ROCK;
      if (foam !== null) {
        // THE MATERIAL IS READ A FEW ROWS UP THE SPRITE, not at the silhouette pixel itself.
        // MEASURED over the lower silhouette edge of all 27 baked islands, that pixel is the
        // sprite's own shaded UNDERSIDE and classifies as rock nearly everywhere - boundary luma
        // p50 39.5 to 78.9 on 25 of them - so reading it gave 17 sand columns and 294 lip columns
        // in the whole world's art. What the foam is under is the SHORE, so the sample walks up
        // to FOAM_MATERIAL_UP rows into the silhouette and takes the brightest land pixel it
        // finds, which is the one a person would call the beach or the quay.
        material = SHELF_ROCK;
        let bestL = -1;
        for (let up = 0; up <= FOAM_MATERIAL_UP; up++) {
          const yy = y - up;
          if (yy < 0 || !mask[yy * w + x]) break;
          const q = (yy * w + x) * 4;
          const c: Rgb = [foam.rgba[q]!, foam.rgba[q + 1]!, foam.rgba[q + 2]!];
          const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
          if (L > bestL) { bestL = L; material = materialOf(c); }
        }
        // SMOOTHSTEP, NOT ITS COMPLEMENT. See the header: the design's `1 - smoothstep` put the
        // foam on the near-vertical ends of a headland and none on the flat shore facing the
        // camera, which measured 0.05% of Murrin Roost's water and 0% of the open sea's.
        const weight = smoothstep(FOAM_NY_LO, FOAM_NY_HI, ny);
        const fringe = FOAM_FRINGE_LO + (1 - FOAM_FRINGE_LO) * foamAt(foam.key, x);
        foamRows = Math.min(BAND_PX, Math.round(foam.widthPx[material]! * weight * fringe));
        if (foamRows > 0) foamColumns[material] = foamColumns[material]! + 1;
      }
      for (let i = 1; i <= Math.max(rows, foamRows, rimHere ? 1 : 0); i++) {
        const py = y + i;
        if (py >= H) break;
        if (py < h && mask[py * w + x]) break; // the water ended: the silhouette resumes below
        const o = (py * w + x) * 4;
        if (i <= foamRows && foamPlane !== null) {
          // FOAM SUPPRESSES THE SHADOW UNDER IT. Surf is not in the island's shade; multiplying
          // the measured ramp over an opaque white would also be the one place in this file
          // where two of the three planes decided one pixel between them.
          const c = foam!.rgb[material]!;
          foamPlane[o] = c[0];
          foamPlane[o + 1] = c[1];
          foamPlane[o + 2] = c[2];
          foamPlane[o + 3] = 255;
          foamPx++;
        } else if (i === 1 && rimHere) {
          rimPlane[o] = rim[0];
          rimPlane[o + 1] = rim[1];
          rimPlane[o + 2] = rim[2];
          rimPlane[o + 3] = 255;
          rimPx++;
        } else if (i <= rows) {
          // a shortened band is the ramp CUT SHORT, not resampled: the near rows keep their
          // measured values and the far ones are simply not drawn
          const k = SHADOW_FACTORS[i - 1] ?? SHADOW_FACTORS[SHADOW_PX - 1]!;
          shadowPlane[o] = k;
          shadowPlane[o + 1] = k;
          shadowPlane[o + 2] = k;
          shadowPlane[o + 3] = 255;
          shadowPx++;
        }
      }
    }
  }
  return { w, h: H, rim: rimPlane, shadow: shadowPlane, foam: foamPlane, rimPx, shadowPx, foamPx, foamColumns };
}
